import { flightTimeScale, useBuilderContext } from "@Context/game/builder/builder-context";
import ScenarioActionType from "@Enums/ScenarioActionType";
import "@pixi/events";
import { Graphics as PixiGraphics } from "@pixi/graphics";
import { Container, Graphics, Sprite } from "@pixi/react";
import Football from "@Resources/game/builder/football.png";
import { isNumber, isEqual } from "lodash-es";
import { useCallback, useEffect, useState } from "react";
import { getPlayer } from "./functions/player-functions";
import { drawArrowhead, drawDashedLine, getPassFlightTime, getPointAtTime, lerp } from "./functions/utilities";
import { BallPoint, BallState } from "./models/builder-data-types";

// const pathColour = 0x000000;
const pathColour = 0xffffff;

const Ball = (props: { size?: number; onDragStart?: () => void; onDragged?: (pos: { x: number; y: number }) => void }) => {
  const { state, dispatch } = useBuilderContext();

  // const [stateChain, setStateChain] = useState<BallState[]>([
  //   { state: 'possessed', time: 0, player: state.ball.initialPossessor },
  // ]);

  const [position, setPosition] = useState(state.clip.scenario.ball.initialPosition);

  useEffect(() => {
    const newChain: BallState[] = [];
    for (let i = 0; i < state.ballStates.length; i++) {
      const newState = { ...state.ballStates[i] };
      if (i === 0) {
        newState.player = state.clip.scenario.ball.initialPossessor;
      }
      newChain.push(newState);
    }
    if (newChain.length == 0) {
      newChain.push({ state: "idle", time: 0, position: state.clip.scenario.ball.initialPosition });
    }
  
    // Only dispatch if the new chain is actually different
    if (!isEqual(newChain, state.ballStates)) {
      dispatch({ ballStates: newChain });
    }
  }, [state.ballStates, state.clip.scenario.ball.initialPossessor, state.clip.scenario.ball.initialPosition]);

  useEffect(() => {
    const passChain: BallState[] = [];
    state.clip.scenario.players.forEach((player) => {
      const actions = player.actions;
      for (let j = 0; j < actions.length; j++) {
        const action = actions[j];
        if (action.type == ScenarioActionType.PassTarget) {
          const passPosition = getPointAtTime(player.points, action.startTime, player.beziers).position;
          const receiver = getPlayer(state.clip, action.targetPlayer);
          if (!receiver) continue;
          const receiverPos = getPointAtTime(receiver.points, action.startTime, receiver.beziers).position;
          const passTime = action.time ? action.time : action.startTime + getPassFlightTime(passPosition, receiverPos, flightTimeScale);
          passChain.push({
            state: "passed",
            time: action.startTime,
            kicker: player.id,
            receiver: action.targetPlayer,
            receiveTime: passTime,
            actionIndex: j,
          });
        } else if (action.type == ScenarioActionType.ShootDirection) {
          const kickPosition = getPointAtTime(player.points, action.startTime, player.beziers).position;
          const targetPosition = action.targetPosition || { x: 0, y: 0 };
          passChain.push({
            state: "kicked",
            time: action.startTime,
            kicker: player.id,
            target: action.targetPosition,
            arriveTime: action.startTime + getPassFlightTime(kickPosition, targetPosition, flightTimeScale * 1.25),
            actionIndex: j,
          });
        }
      }
    });

    passChain.sort((a, b) => a.time - b.time);
    const newChain: BallState[] = [];
    newChain.push({ state: "possessed", time: 0, player: state.clip.scenario.ball.initialPossessor });
    for (let i = 0; i < passChain.length; i++) {
      newChain.push(passChain[i]);
      if (passChain[i].state == "passed") 
        newChain.push({ state: "possessed", time: passChain[i].receiveTime || 0, player: passChain[i].receiver });
    }

    // console.log('New chain', newChain);
    
    // Only dispatch if the new chain is actually different from current ballStates
    if (!isEqual(newChain, state.ballStates)) {
      dispatch({ ballStates: newChain });
    }
  }, [state.clip.scenario.players, state.clip.scenario.ball]);

  useEffect(() => {
    let found = false;
    let stateIndex = 0;
    for (let i = 0; i < state.ballStates.length - 1; i++) {
      const ballState = state.ballStates[i];
      if (ballState.time <= state.time && state.ballStates[i + 1].time >= state.time) {
        stateIndex = i;
        found = true;
        break;
      }
    }
    if (!found) {
      stateIndex = state.ballStates.length - 1;
    }

    const ballState = state.ballStates[stateIndex];

    if (ballState.state === "idle" && ballState.position) {
      setPosition(ballState.position);
      return;
    }

    if (ballState.state === "possessed") {
      if (isNumber(ballState.player)) {
        const player = getPlayer(state.clip, ballState.player);
        if (player) {
          const playerPos = getPointAtTime(player.points, state.time, player.beziers);
          setPosition(playerPos.position);
          return;
        }
      }
    }

    if (ballState.state === "passed" && isNumber(ballState.kicker) && isNumber(ballState.receiver) && isNumber(ballState.receiveTime)) {
      const kicker = getPlayer(state.clip, ballState.kicker);
      const receiver = getPlayer(state.clip, ballState.receiver);
      if (!kicker || !receiver) return;
      const kickerPos = getPointAtTime(kicker.points, ballState.time, kicker.beziers);
      const receiverPos = getPointAtTime(receiver.points, ballState.receiveTime, receiver.beziers);
      const t = (state.time - ballState.time) / (ballState.receiveTime - ballState.time);
      const ballPos = { x: lerp(kickerPos.position.x, receiverPos.position.x, t), y: lerp(kickerPos.position.y, receiverPos.position.y, t) };
      setPosition(ballPos);
      return;
    }

    if (ballState.state === "kicked" && isNumber(ballState.kicker) && ballState.target && isNumber(ballState.arriveTime)) {
      const kicker = getPlayer(state.clip, ballState.kicker);
      if (!kicker) return;
      const kickerPos = getPointAtTime(kicker.points, ballState.time, kicker.beziers);
      const t = (state.time - ballState.time) / (ballState.arriveTime - ballState.time);
      const ballPos = { x: lerp(kickerPos.position.x, ballState.target.x, t), y: lerp(kickerPos.position.y, ballState.target.y, t) };
      setPosition(ballPos);
      return;
    }

    setPosition(state.clip.scenario.ball.initialPosition);
  }, [state.ballStates, state.time]);

  const onDragStart = (event: any) => {
    if (state.time > 0) return;
    const sprite = event.currentTarget;
    sprite.alpha = 0.5;
    sprite.data = event.data;
    sprite.dragging = true;
    if (props.onDragStart) props.onDragStart();
  };

  const onDragMove = (event: any) => {
    if (state.time > 0) return;
    const sprite = event.currentTarget;
    if (sprite.dragging) {
      const newPosition = sprite.data.getLocalPosition(sprite.parent);
      sprite.x = newPosition.x;
      sprite.y = newPosition.y;
    }
  };

  const onDragEnd = (event: any) => {
    if (state.time > 0) return;
    const sprite = event.currentTarget;
    sprite.alpha = 1;
    sprite.dragging = false;
    sprite.data = null;
    if (props.onDragged) props.onDragged({ x: sprite.x, y: sprite.y });
  };

  const drawPath = useCallback(
    (g: PixiGraphics) => {
      g.clear();
      for (let i = 0; i < state.ballStates.length; i++) {
        const isActionSelected = state.selectedPlayer === state.ballStates[i].kicker && state.ballStates[i].actionIndex === state.selectedAction;
        const ballState = state.ballStates[i];
        const nextBallState = state.ballStates[i + 1];
        const nextTime = nextBallState ? nextBallState.time : state.clip.scenario.endTime;
        const alphaScale = 5;

        let midT = i === 0 ? 0 : (ballState.time + nextTime) / 2;
        let timeDiff = Math.min(alphaScale, Math.abs(state.time - midT)) / alphaScale;
        let alpha = isActionSelected ? 1 : Math.max(0, 1 - timeDiff);

        // for possessions draw a solid line that follows the route
        if (ballState.state === "possessed" && isNumber(ballState.player)) {
          const player = getPlayer(state.clip, ballState.player);
          if (!player) {
            //console.log("Player not found", ballState.player, state.clip);
            continue;
          }
          const playerPos = getPointAtTime(player.points, ballState.time, player.beziers);

          // sample position every 0.05 seconds
          const dt = 0.05;
          let fromPos = playerPos;
          for (let t = ballState.time; t < nextTime; t += dt) {
            let timeDiff = Math.min(alphaScale, Math.abs(state.time - t)) / alphaScale;
            let alpha = Math.max(0, 1 - timeDiff);
            g.lineStyle(2 * state.spriteScale, pathColour, alpha);
            g.moveTo(fromPos.position.x, fromPos.position.y);
            const pos = getPointAtTime(player.points, t, player.beziers);
            g.lineTo(pos.position.x, pos.position.y);
            fromPos = pos;
          }

          // draw a line to the next state
          g.moveTo(fromPos.position.x, fromPos.position.y);
          const nextPlayerPos = getPointAtTime(player.points, nextTime, player.beziers);
          g.lineTo(nextPlayerPos.position.x, nextPlayerPos.position.y);
        }

        // for passes draw a dashed arrow to the receiver
        if (ballState.state === "passed" && isNumber(ballState.kicker) && isNumber(ballState.receiver) && isNumber(ballState.receiveTime)) {
          const kicker = getPlayer(state.clip, ballState.kicker);
          const receiver = getPlayer(state.clip, ballState.receiver);
          if (!kicker || !receiver) continue;
          const kickerPos = getPointAtTime(kicker.points, ballState.time, kicker.beziers);
          const receiverPos = getPointAtTime(receiver.points, ballState.receiveTime, receiver.beziers);
          g.lineStyle(3 * state.spriteScale, pathColour, 0.66 * alpha);
          g.moveTo(kickerPos.position.x, kickerPos.position.y);
          drawDashedLine(
            g,
            kickerPos.position.x,
            kickerPos.position.y,
            receiverPos.position.x,
            receiverPos.position.y,
            5 * state.spriteScale,
            5 * state.spriteScale
          );
          // drawTemporalDashedLine(g, kickerPos.position.x, kickerPos.position.y, receiverPos.position.x, receiverPos.position.y, 5 * state.spriteScale, 5 * state.spriteScale, ballState.time, ballState.receiveTime, state.time, alphaScale, 2 * state.spriteScale, pathColour);
          g.lineStyle(3 * state.spriteScale, pathColour, alpha);
          drawArrowhead(g, kickerPos.position, receiverPos.position, 15 * state.spriteScale);
        }

        // for kicks draw a dashed arrow to the target
        if (ballState.state === "kicked" && isNumber(ballState.kicker) && ballState.target && isNumber(ballState.arriveTime)) {
          const kicker = getPlayer(state.clip, ballState.kicker);
          if (!kicker) continue;
          const kickerPos = getPointAtTime(kicker.points, ballState.time, kicker.beziers);
          g.lineStyle(3 * state.spriteScale, pathColour, 0.66 * alpha);
          g.moveTo(kickerPos.position.x, kickerPos.position.y);
          drawDashedLine(
            g,
            kickerPos.position.x,
            kickerPos.position.y,
            ballState.target.x,
            ballState.target.y,
            5 * state.spriteScale,
            5 * state.spriteScale
          );
          g.lineStyle(3 * state.spriteScale, pathColour, alpha);
          drawArrowhead(g, kickerPos.position, ballState.target, 15 * state.spriteScale);
        }
      }
    },
    [state.ballStates, state.clip.scenario.players, state.spriteScale, state.time, state.options.pathLength, state.selectedAction, state.selectedPlayer]
  );

  return (
    <Container>
      <Sprite
        image={Football}
        x={position.x}
        y={position.y}
        anchor={0.5}
        width={(props.size || 32) * state.spriteScale}
        height={(props.size || 32) * state.spriteScale}
        eventMode={state.time <= 0 ? "dynamic" : "passive"}
        pointerdown={onDragStart}
        onglobalpointermove={onDragMove}
        pointerup={onDragEnd}
        pointerupoutside={onDragEnd}
      />
      {state.options.showBallPath && <Graphics draw={drawPath} />}
    </Container>
  );
};

export default Ball;
